import React, {useState} from "react";
import {useQueryClient} from "react-query";
import {useNavigate} from "react-router-dom";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {tokenSelector} from "../../Recoil/Atom/authState";

import {useGetQuery, useRemoveMutation} from "../../hooks/useQueryHooks";
import {ResponseList, ResponseRemove} from "../../models/clientModels/interfaces/Response";
import * as Params from "../../models/clientModels/interfaces/apiParams";

import {
  Box,
  Button,
  Link,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled,
} from "@mui/material";
import {alertStateAtom} from "../../Recoil/Atom/AlertAtom";
import MWSkeleton from "../../components/MWSkeleton";
import SubTitle from "../../components/SubTitle";
import {useDepartmentList} from "../../hooks/useDepartmentList";
import {initializeUserListParams} from "../../models/serverModels/EQUser";
import {
  StyledButton,
  StyledButtonGroup,
  SubMenuGroup,
} from "../../styledComponents/StyledListContainer";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";

import {PaginationContainer} from "../../styledComponents/StyledPagination";
import {EQCompany, initialCompanyListParams} from "../../models/serverModels/EQCompany";

import * as companyApi from "../../api/companyApi/companyApi";
import {StyledTableContainer, StyledTableHead} from "../../styledComponents/StyledTable";

const ListContainer = styled(Box)(({theme}) => ({
  minWidth: "1200px",
}));

const BoardSupervisorList = () => {
  const tokenState = useRecoilValue(tokenSelector);

  const [page, setPage] = useState<number>(1);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setAlertState = useSetRecoilState(alertStateAtom);

  const {mutate: removeMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: companyApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("CompanyList");
      },
    },
  });

  const {
    data: companyList,
    isLoading,
    isFetching,
  } = useGetQuery<ResponseList<EQCompany>>({
    key: ["CompanyList", {...initialCompanyListParams, page, token: tokenState}],
    api: () => companyApi.getList({...initialCompanyListParams, page, token: tokenState}),
  });

  const paginationHandleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };
  return (
    <ListContainer>
      <SubTitle subTitle="설비관리 업체 목록" />
      <SubMenuGroup>
        <Box></Box>
        <StyledButtonGroup>
          <StyledButton
            variant="outlined"
            size="medium"
            color="primary"
            startIcon={<LibraryAddIcon />}
            sx={{whiteSpace: "nowrap", flexShrink: 0}}
            onClick={() => navigate("/Company/Create")}
          >
            새로추가
          </StyledButton>
        </StyledButtonGroup>
      </SubMenuGroup>
      <StyledTableContainer>
        <Table>
          <StyledTableHead>
            <TableRow>
              <TableCell align="center">#</TableCell>
              <TableCell align="center">업체명</TableCell>
              <TableCell align="center">연락처</TableCell>
              <TableCell align="center">주소</TableCell>
              <TableCell align="center">등록날짜</TableCell>
              <TableCell align="center">수정날짜</TableCell>
              <TableCell align="center" width="15%">
                편집
              </TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {isLoading && isFetching ? (
              <MWSkeleton row={12} column={7} />
            ) : (
              companyList?.items.map((item, index) => (
                <TableRow key={item.uid}>
                  <TableCell align="center">
                    {page === 1
                      ? index + 1
                      : (page - 1) * initializeUserListParams().rows! + index + 1}
                  </TableCell>
                  <TableCell align="center">{item.name}</TableCell>
                  <TableCell align="center">{item.phone}</TableCell>
                  <TableCell align="center">{item.address}</TableCell>
                  <TableCell align="center">
                    {new Date(item?.entryDate).getFullYear()}년
                    {new Date(item?.entryDate).getMonth() + 1}월
                    {new Date(item?.entryDate).getDate()}일
                  </TableCell>
                  <TableCell align="center">
                    {new Date(item?.modifyDate).getFullYear()}년
                    {new Date(item?.modifyDate).getMonth() + 1}월
                    {new Date(item?.modifyDate).getDate()}일
                  </TableCell>

                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      style={{marginRight: "12px"}}
                      onClick={() => {
                        navigate(`/Company/Edit/${item.uid}`);
                      }}
                    >
                      편집
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={() => {
                        setAlertState({
                          toggle: true,
                          message: "등록된 업체를 삭제하시겠습니까?",
                          execution: () => removeMutate({token: tokenState, id: item.uid!}),
                        });
                      }}
                    >
                      삭제
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
      {/* 페이지네이션 */}
      <PaginationContainer>
        <Pagination
          count={companyList?.maxPage}
          page={page}
          onChange={paginationHandleChange}
          variant="outlined"
          color="primary"
          shape="rounded"
        />
      </PaginationContainer>
    </ListContainer>
  );
};

export default BoardSupervisorList;
